import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onUnmounted,
  PropType,
  nextTick,
  onActivated,
  onDeactivated,
  inject,
  toRefs,
} from 'vue';
import { UBT } from '@hb/tianqi/lib/ubt.h5';
import logger from '~mall/common/utils/logger';
import { getStylesBinder } from '~mall/common/utils/styles';
import styles from './index.module.less';
import { appQueryConfig } from '~mall/common/utils/url';
import { CouponMallTabEnum } from '../../enum';

const cx = getStylesBinder(styles);

/** Tab选项类型 */
export interface TabOptionType {
  categoryId: string;
  categoryName: string;
}

export default defineComponent({
  name: 'SelectTab',
  props: {
    titleBarHeight: {
      type: Number,
      default: 0,
    },
    tabList: {
      type: Array as PropType<TabOptionType[]>,
      required: true,
      default: () => [],
    },
    defaultSelect: {
      type: String,
      default: CouponMallTabEnum.ALL,
    },
    handleTabChange: {
      type: Function as PropType<(row: TabOptionType) => void>,
      default: () => undefined,
    },
    recommendLoaded: {
      type: Boolean,
      default: false,
    },
  },
  setup: (props, { expose }) => {
    const showAllTab = ref(false);
    const curIndex = ref(0);
    const curTab = ref('');
    const dynamicRef = ref<HTMLElement | null>(null);
    const { titleBarHeight } = toRefs(props);
    const styleVersion = inject('styleVersion');

    // 获取 titleBarHeight 转换为 rem 后的实际像素值
    const getActualTitleBarHeight = () => {
      const rootFontSizeStr = window.getComputedStyle(document.documentElement)?.fontSize;
      const rootFontSize = parseFloat(rootFontSizeStr);
      if (Number.isNaN(rootFontSize) || rootFontSize <= 0) {
        return titleBarHeight.value; // 兜底：直接返回原始值
      }
      return (titleBarHeight.value / 100) * rootFontSize;
    };

    // 吸顶相关逻辑
    const placeholderRef = ref<HTMLElement | null>(null); // 新增：指向外部占位元素
    const isFixed = ref(false); // 是否吸顶
    const _lastActiveTop = ref(0); // 上一次页面活动时的滚动距离，用于恢复滚动位置
    const _tabStickyTop = ref(0); // tab吸顶需要滚动的距离，用于滚动到顶部
    const scrollContainer = ref<HTMLElement | null>(null); // 保存滚动容器元素
    let resizeObserver: ResizeObserver | null = null; // 监听上方内容高度变化

    // 将元素平滑滚动到可视区域中间
    const scrollElementToCenter = (element: HTMLElement | null) => {
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest', // 垂直方向"就近"对齐，避免滚动到页面顶部
        });
      }
    };

    // 滚动距离公共方法
    const scrollTo = (toTop: number) => {
      // 每次都重新获取滚动容器，确保获取到正确的元素
      const el = document.querySelector(
        '.welfare-center-content',
      ) as HTMLElement;

      if (el) {
        logger('滚动到', toTop);
        el.scrollTop = toTop;
      }
    };

    // 监听滚动事件
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target || !target.className) return;
      if (!target.className.includes('welfare-center-content')) {
        return;
      }
      // 保存滚动容器引用
      if (
        target
        && target !== document.documentElement
        && target !== document.body
      ) {
        scrollContainer.value = target;
      }
      // 记录滚动距离
      _lastActiveTop.value = target?.scrollTop || 0;

      if (placeholderRef.value) {
        const { top } = placeholderRef.value.getBoundingClientRect();
        // 当占位元素滚动到顶部时（top <= 0），设置吸顶状态
        isFixed.value = top <= getActualTitleBarHeight();
      }
    };

    const handleToggleAll = () => {
      if (!showAllTab.value) {
        // 展开时，如果还没吸顶，手动将页面滚动到吸顶位置
        if (!isFixed.value && placeholderRef.value) {
          scrollTo(_tabStickyTop.value);
        }
      }
      showAllTab.value = !showAllTab.value;
    };

    // 更新选中状态（不滚动、不调回调）
    const updateTabSelectState = (row: TabOptionType, index: number) => {
      if (!row) return;
      curIndex.value = index; // index变化tab滑动到中间
      curTab.value = row.categoryName; // 埋点用
    };

    // 处理 tab 选中后（滚动、回调、弹窗）
    const handleTabSelectSideEffect = (row: TabOptionType, type: string) => {
      if (!row) return;

      // 1. 执行父组件回调
      if (typeof props.handleTabChange === 'function') {
        props.handleTabChange(row);
      }

      // 2. 根据类型处理滚动/弹窗
      switch (type) {
        case 'pop-tab':
          handleToggleAll(); // 关闭弹窗
          scrollTo(_tabStickyTop.value); // 滚动到吸顶位置
          break;
        case 'tab':
          if (isFixed.value) { scrollTo(_tabStickyTop.value); } // 如果吸顶滚动到吸顶位置，如果还没吸顶就不动
          break;
        // 初始化时（type 为空）：不处理滚动/弹窗
        default:
          break;
      }

      // 3. tab点击埋点
      UBT.clickButton({
        pageId: 'platform_couponmall',
        buttonName: 'platform_listTab',
        categoryId: 'platform',
        businessInfo: {
          experimentalGroup: styleVersion,
          adsource: appQueryConfig.adsource,
          tabNum: curIndex.value + 1,
          tabName: curTab.value,
        },
      });
    };

    // 点击tab
    const handleChooseItemTab = (row: TabOptionType, index: number, type: string) => {
      // 1. 更新选中状态
      updateTabSelectState(row, index);
      // 2. 处理
      handleTabSelectSideEffect(row, type);
    };

    const handleClickPop = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target || !target.className) return;
      if (target.className.includes('all_tab-wrapper')) {
        if (e.target === e.currentTarget) {
          handleToggleAll();
        }
      }
    };

    // Watch curIndex to scroll
    watch(curIndex, () => {
      nextTick(() => {
        scrollElementToCenter(dynamicRef.value);
      });
    });

    // Watch 监听 tabList/defaultSelect，初始化
    watch(
      [() => props.tabList, () => props.defaultSelect],
      () => {
        if (!props.tabList || props.tabList.length === 0) return;

        // 1. 找到默认选中的 tab
        const targetItem = props.tabList.find(
          (item) => item.categoryId === props.defaultSelect,
        );
        const targetIndex = props.tabList.findIndex(
          (item) => item.categoryId === props.defaultSelect,
        );

        if (targetItem === undefined || targetIndex === -1) return;

        // 3. 先更新状态，再处理
        updateTabSelectState(targetItem, targetIndex);
        handleTabSelectSideEffect(targetItem, ''); // type 为空，不滚动/关弹窗，其实只为了执行父组件回调
      },
      { immediate: true },
    );

    // 监听 CommonRecommend 数据加载完成，重新计算吸顶距离
    watch(
      () => props.recommendLoaded,
      (loaded) => {
        if (loaded) {
          nextTick(() => {
            const top = placeholderRef.value?.getBoundingClientRect().top ?? 0;
            const currentScrollTop = scrollContainer.value?.scrollTop || 0;
            _tabStickyTop.value = currentScrollTop + top - getActualTitleBarHeight() + 1;
          });
        }
      },
      { immediate: true },
    );

    const bindScroll = () => {
      window.removeEventListener('scroll', handleScroll, true); // 防止重复绑定
      window.addEventListener('scroll', handleScroll, true);
    };

    const unbindScroll = () => {
      window.removeEventListener('scroll', handleScroll, true);
    };

    onMounted(() => {
      bindScroll();
      // 获取滚动容器
      const container = document.querySelector(
        '.welfare-center-content',
      ) as HTMLElement;
      if (container) {
        scrollContainer.value = container;
      }

      // tab曝光埋点
      if (props.tabList.length > 0) {
        props.tabList.forEach((item, index) => {
          UBT.moduleExpose({
            pageId: 'platform_couponmall',
            categoryId: 'platform',
            moduleId: 'platform_listTab',
            contentId: 'platform_listTab',
            exposureTimes: 1,
            businessInfo: {
              experimentalGroup: styleVersion,
              adsource: appQueryConfig.adsource,
              tabNum: index + 1,
              tabName: item.categoryName,
            },
          });
        });
      }
    });

    onUnmounted(() => {
      unbindScroll();
      // 清理 ResizeObserver
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    });

    onActivated(() => {
      bindScroll();
      // 恢复上一次页面活动时的滚动距离
      logger('恢复滚动位置', _lastActiveTop.value);
      // 使用 nextTick 确保 DOM 已经渲染/显示
      nextTick(() => {
        scrollTo(_lastActiveTop.value);
        scrollElementToCenter(dynamicRef.value);
      });
    });

    onDeactivated(() => {
      // 记录上一次页面活动时的滚动距离
      logger('记录滚动位置', _lastActiveTop.value);
      unbindScroll();
    });

    // 监听 tab 切换事件
    const onTabChange = () => {
      // 如果监听到这个事件被触发说明是领奖励金和优惠卡券tab被切换，那么要滚动到0
      _lastActiveTop.value = 0;
    };

    // 暴露方法给父组件
    expose({
      onTabChange,
    });

    return () => (
      <div ref={placeholderRef} class={cx('tab-placeholder', { fixed: isFixed.value })}>
        <div
          class={cx('tab-wrapper', { fixed: isFixed.value })}
          style={{ '--coupon-title-bar-height': `${titleBarHeight.value / 100}rem` }}
        >
          {/* 业务线tab+展开按钮 */}
          <div class={cx('tab-content')}>
            <div class={cx('item-wrapper')}>
              {props.tabList.map((item, index) => {
                if (!item) return null;
                return (
                  <span
                    key={item.categoryId}
                    class={cx('item-tab', { action: index === curIndex.value })}
                    // 只有当前选中的项，才会把 DOM 赋值给 dynamicRef
                    ref={index === curIndex.value ? dynamicRef : undefined}
                    onClick={() => handleChooseItemTab(item, index, 'tab')}
                  >
                    {item.categoryName}
                  </span>
                );
              })}
            </div>
            {props.tabList.length > 0 && (
              <img
                class={cx('arrow-icon')}
                src="https://m.hellobike.com/53cf72ed_Group%202090054338.svg"
                onClick={() => {
                  // 点击埋点
                  UBT.clickButton({
                    pageId: 'platform_couponmall',
                    buttonName: 'platform_listTab_gongge',
                    categoryId: 'platform',
                    businessInfo: {
                      experimentalGroup: styleVersion,
                      adsource: appQueryConfig.adsource,
                    },
                  });
                  // 更多业务展开曝光埋点
                  UBT.moduleExpose({
                    pageId: 'platform_couponmall',
                    categoryId: 'platform',
                    moduleId: 'platform_listTab_gongge',
                    contentId: 'platform_listTab_gongge',
                    exposureTimes: 1,
                    businessInfo: {
                      experimentalGroup: styleVersion,
                      adsource: appQueryConfig.adsource,
                    },
                  });
                  handleToggleAll();
                }}
              />
            )}
          </div>
          {/* 点击展开后显示全部业务线tab */}
          {showAllTab.value ? (
            <div
              class={cx('all_tab-wrapper')}
              onClick={handleClickPop}
              onTouchmove={(e: TouchEvent) => e.preventDefault()}
            >
              <div class={cx('all_tab-title_wrapper')}>
                <p class={cx('all_tab-title')}>全部业务</p>
                <img
                  class={cx('all_tab-close')}
                  onClick={handleToggleAll}
                  src="https://m.hellobike.com/resource/helloyun/14121/x-YKZ_close.png"
                  alt=""
                />
              </div>
              <div class={cx('tab-container')}>
                {props.tabList.map((item, index) => {
                  if (!item) return null;
                  return (
                    <span
                      key={item.categoryId as string}
                      class={cx('item-tab_all', {
                        action: index === curIndex.value,
                      })}
                      onClick={() => handleChooseItemTab(item, index, 'pop-tab')
                      }
                    >
                      {item.categoryName}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
});
